import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { getInformation } from '../api';
import Header from '../components/Header';
import MenuBar from '../components/MenuBar';
import ProtectedRoute from '../components/ProtectedRoute';
import Report from '../components/Report';
import { featuresContext, temperatureContext } from '../lib/context';
import './Common.css';
import DownloadPage from './Download';
import Forgot from './Forgot';
import Login from './Login';
import OverviewPage from './Overview';
import PendingPage from './Pending';
import RealtimePage from './Realtime';
import RecordsPage from './Records';
import Reset from './Reset';
import Setting from './Setting';
import Signup from './Signup';
import Verify from './Verify';

function Home() {
    const isClient = typeof window !== 'undefined';

    const { t, i18n } = useTranslation();
    // Must match the API's spelling: a misspelled default always differed from the
    // fetched 'celsius', re-running the effect below and every unit-dependent view (#272).
    const [tempUnit, setTempUnit] = useState('celsius');
    const [info, setInfo] = useState<any>({});
    const [color] = useState('light');
    const [companyName, setCompanyName] = useState('');
    const [login, setLogin] = useState(false);
    const [isMobile, setIsMobile] = useState(isClient ? window.innerWidth <= 768 : false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [desktopExpanded, setDesktopExpanded] = useState(false);
    const [features, setFeatures] = useState<string[]>([]);
    // Tracks the token whose account-language preference we've already applied, so the
    // effect below (which refetches multiple times) applies it once per login instead
    // of on every run — otherwise it re-overwrites a manual language switch, e.g. on the
    // public Download page, making the language selector appear broken when logged in.
    const appliedLangTokenRef = useRef<string | null>(null);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            getInformation().then((data: any) => {
                if (data && data.company && data.user) {
                    setInfo({ ...data.company, ...data.user });
                    setCompanyName(data.company.display_name);
                    setTempUnit(data.user.preference.temp_unit);
                    if (appliedLangTokenRef.current !== token) {
                        i18n.changeLanguage(data.user.preference.language);
                        appliedLangTokenRef.current = token;
                    }
                    setFeatures(data.user.features || []);
                }
            });
        }
    }, [login, tempUnit, i18n]);
    // Load multiple time. Any way to solve this?

    useEffect(() => {
        if (!isClient) {
            return;
        }
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        handleResize();
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [isClient]);

    useEffect(() => {
        if (!isMobile) {
            setMobileMenuOpen(false);
        } else {
            setDesktopExpanded(false);
        }
    }, [isMobile]);

    function handleCompanyNameChange(name: any) {
        setCompanyName(name);
    }

    function handlePreferenceChange(language: any, temp_unit: any) {
        i18n.changeLanguage(language);
        setTempUnit(info.preference.tempUnit);
        setInfo({ ...info, preference: { ...info.preference, language, temp_unit } });
    }

    const homeStyle = useMemo(
        () => ({
            width: '100%',
            minHeight: '100vh',
            backgroundColor: color === 'light' ? '#F3F5F9' : '#262729',
        }),
        [color]
    );

    const contentStyle = useMemo(
        () =>
            isMobile
                ? { marginLeft: 0, paddingBottom: '40px' }
                : {
                      marginLeft: desktopExpanded ? '320px' : '80px',
                      paddingBottom: '40px',
                      transition: 'margin-left 300ms ease',
                  },
        [isMobile, desktopExpanded]
    );

    const pageWrapperStyle = useMemo(() => (isMobile ? { marginTop: '16px' } : { marginTop: '24px' }), [isMobile]);

    const handleMenuToggle = () => {
        setMobileMenuOpen((prev: any) => !prev);
    };

    const handleMenuClose = () => {
        setMobileMenuOpen(false);
    };

    const handleDesktopMenuToggle = () => {
        setDesktopExpanded((prev: any) => !prev);
    };

    const renderWithShell = (title: any, element: any) => (
        <ProtectedRoute>
            <div>
                <MenuBar
                    color={color}
                    info={info}
                    isMobile={isMobile}
                    desktopExpanded={desktopExpanded}
                    onDesktopToggle={handleDesktopMenuToggle}
                    open={isMobile ? mobileMenuOpen : undefined}
                    onClose={handleMenuClose}
                />
                <div style={contentStyle}>
                    <Header
                        title={title}
                        companyName={companyName}
                        setLogin={setLogin}
                        color={color}
                        isMobile={isMobile}
                        onMenuToggle={handleMenuToggle}
                    />
                    <div style={pageWrapperStyle}>{element}</div>
                </div>
            </div>
        </ProtectedRoute>
    );

    return (
        <div className='Home' style={homeStyle}>
            <featuresContext.Provider value={features}>
                <temperatureContext.Provider value={tempUnit}>
                    <BrowserRouter>
                        <Routes>
                            <Route path='/' element={<Navigate to='/overview' />} />
                            <Route
                                path='/login'
                                element={<Login setCompanyName={setCompanyName} setLogin={setLogin} />}
                            />
                            <Route
                                path='/forgot'
                                element={<Forgot setCompanyName={setCompanyName} setLogin={setLogin} />}
                            />
                            <Route
                                path='/signup'
                                element={<Signup setCompanyName={setCompanyName} setLogin={setLogin} />}
                            />
                            <Route path='/verify' element={<Verify />} />
                            <Route path='/reset' element={<Reset />} />
                            <Route
                                path='/overview'
                                element={renderWithShell(t('Overview.title'), <OverviewPage isMobile={isMobile} />)}
                            />
                            <Route path='/pending' element={renderWithShell(t('Pending.title'), <PendingPage />)} />
                            <Route path='/uploaded' element={renderWithShell(t('Records.title'), <RecordsPage />)} />
                            <Route
                                path='/realtime'
                                element={renderWithShell(
                                    t('RealTime.title'),
                                    <RealtimePage isVendor={!!info.is_vendor} />
                                )}
                            />
                            <Route
                                path='/setting'
                                element={renderWithShell(
                                    t('Information.title'),
                                    <Setting
                                        handlePreferenceChange={handlePreferenceChange}
                                        info={info}
                                        handleCompanyNameChange={handleCompanyNameChange}
                                    />
                                )}
                            />
                            <Route path='/report' element={renderWithShell('報告 Report', <Report />)} />
                            <Route path='/download' element={<DownloadPage />} />
                        </Routes>
                    </BrowserRouter>
                </temperatureContext.Provider>
            </featuresContext.Provider>
        </div>
    );
}

export default Home;
